import SublineModel from '@/editor/entities/2d/sublineModel';
import {
  IAbsorbResult,
  IBoxBoundingLineInfo,
  ICalculateOptions,
  ICenterLineInfo,
  RotatedBoxAbsorbCalculator
} from './rotatedBoxAbsorbCalculator';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { globalStores } from '@/editor/stores';
import { EColumnType } from '@/editor/data/model/model';
import { convertParallelAbsorbSeb, createBoxParallelAbsorbSegs, getLineAngle } from './utils';
import { modelManager } from '../../entityManager';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { IRotatedBox } from '@/editor/types';

export class ColumnAbsorbManager {
  private _calculator = new RotatedBoxAbsorbCalculator();
  private _sublineModel = new SublineModel();

  constructor() {}

  updateData() {
    const absorbBoxBoundingLines: IBoxBoundingLineInfo[] = [];
    const absorbCenterLines: ICenterLineInfo[] = [];

    globalStores.connectedWallOutlines.forEach((outline) => {
      outline.edges.forEach((edge) => {
        if (edge.center) return;
        if (Point2dUtils.equals(edge.start, edge.end)) return;

        const seg = new LineSeg2d(edge.start, edge.end);
        const line = seg.createLine();
        absorbBoxBoundingLines.push({
          seg,
          line,
          rotatable: true,
          onlyAbsorbSeg: true,
          angle: getLineAngle(line)
        });
      });
    });

    // 添加柱子的中心线和边界线
    globalStores.structStore.getColumnDatas().forEach((box) => {
      const rotatedBox = {
        center: box.center,
        width: box.type === EColumnType.cylinder ? box.radius * 2 : box.width,
        thickness: box.type === EColumnType.cylinder ? box.radius * 2 : box.thickness,
        rotation: box.rotation
      };

      const { centerLines, boxBoundingLines } = createBoxParallelAbsorbSegs(rotatedBox);
      centerLines.forEach((centerLine) => {
        absorbCenterLines.push(centerLine);
      });
      boxBoundingLines.forEach((boxBoundingLine) => {
        absorbBoxBoundingLines.push(boxBoundingLine);
      });
    });

    this._calculator.updateData({
      absorbBoxBoundingLines,
      absorbCenterLines
    });
  }

  calculateThenRender(rawBox: IRotatedBox) {
    this.clearModel();

    const calculateOptions: ICalculateOptions = {};
    if (globalStores.paramStore.floorPlanConfig && globalStores.paramStore.floorPlanConfig.type === 'dxf') {
      const extraBoxBoundingLineInfos: IBoxBoundingLineInfo[] = [];
      const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;
      const radius = Math.max(rawBox.width, rawBox.thickness) / 2 + dynamicAbsorbDistance;
      const dxfLines = modelManager.floorPlanManager.getNearLines(rawBox.center.x, rawBox.center.y, radius);
      if (dxfLines) {
        for (let i = 0; i < dxfLines.length; i += 4) {
          const start = { x: dxfLines[i], y: dxfLines[i + 1] };
          const end = { x: dxfLines[i + 2], y: dxfLines[i + 3] };
          const seg = new LineSeg2d(start, end);
          if (seg.length < 200) continue; // TODO: 待优化，最好在dxf解析时过滤掉
          const line = seg.createLine();
          extraBoxBoundingLineInfos.push({
            seg,
            line,
            angle: getLineAngle(line),
            rotatable: true,
            onlyAbsorbSeg: true
          });
        }
      }
      if (extraBoxBoundingLineInfos.length > 0) {
        calculateOptions.extraBoxBoundingLineInfos = extraBoxBoundingLineInfos;
        calculateOptions.dynamicAbsorbDistance = dynamicAbsorbDistance;
      }
    }

    const absorbResult = this._calculator.calculate(rawBox, calculateOptions);
    if (!absorbResult) return;

    const newBox = {
      ...rawBox,
      center: absorbResult.absorbPoint,
      rotation: absorbResult.rotation
    };

    this.updateModel(absorbResult, newBox);
    return absorbResult;
  }

  private updateModel(absorbResult: IAbsorbResult, box: IRotatedBox) {
    this._sublineModel.update(convertParallelAbsorbSeb(absorbResult.absorbSegInfos, box));
  }

  clearModel() {
    this._sublineModel.clear();
  }

  dispose() {
    this._sublineModel.dispose();
  }
}
